import { createAutocompleteController } from '../autocomplete-controller.js';
import { requireElement, selectTextInput } from '../dom-binding.js';

export interface ToolbarSearchOptions<T> {
  loadSuggestions: (query: string, signal: AbortSignal) => Promise<T[]>;
  getLabel: (suggestion: T) => string;
  onSelect: (suggestion: T, query: string) => void | Promise<void>;
  onSubmit: (query: string) => void | Promise<void>;
  onButtonSubmit?: (query: string) => void | Promise<void>;
  onInput?: () => void;
  onError?: (cause: unknown, query: string) => void;
  onDismiss?: () => void;
  minQueryLength?: number;
  debounceMs?: number;
}

export interface ToolbarSearchPort {
  configureSearch<T>(options: ToolbarSearchOptions<T>): void;
  setSearchValue(value: string): void;
  blurSearch(): void;
  focusSearch(): void;
  prefillSearch(query: string): void;
  closeSearch(): void;
}

export class ToolbarSearchController {
  private readonly root: ParentNode;
  private input: HTMLInputElement | null = null;
  private button: HTMLButtonElement | null = null;
  private interactionRoot: Element | null = null;
  private autocomplete: { close(): void } | null = null;
  private submitSearch: ((query: string) => void | Promise<void>) | null = null;
  private submitButtonSearch: ((query: string) => void | Promise<void>) | null = null;
  private dismissSearch: (() => void) | null = null;
  private selectionClickUntil = 0;
  private readonly onButtonClick = () => this.submit();
  private readonly onDocumentClick = (event: MouseEvent) => this.dismissIfOutside(event);
  private readonly onInputKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this.closeSearch();
    this.dismissSearch?.();
    this.input?.blur();
  };

  constructor(root: ParentNode) {
    this.root = root;
  }

  configureSearch<T>(options: ToolbarSearchOptions<T>): void {
    const input = requireElement('#search-input', HTMLInputElement, this.root);
    const button = this.root.querySelector<HTMLButtonElement>('#search-btn');
    const suggestionsElement = requireElement('#search-suggestions', HTMLElement, this.root);
    this.autocomplete?.close();
    this.button?.removeEventListener('click', this.onButtonClick);
    this.input?.removeEventListener('keydown', this.onInputKeydown);
    this.input = input;
    this.button = button;
    this.interactionRoot = input.closest('.search-container')
      ?? (this.root instanceof Element ? this.root : null);
    this.submitSearch = options.onSubmit;
    this.submitButtonSearch = options.onButtonSubmit ?? null;
    this.dismissSearch = options.onDismiss ?? null;
    this.autocomplete = createAutocompleteController<T>({
      input,
      suggestionsElement,
      loadSuggestions: options.loadSuggestions,
      getLabel: options.getLabel,
      onSelect: (suggestion, query) => {
        // A touch selection removes its result before the compatibility click fires.
        // Keep that delayed click from being treated as an outside dismissal.
        this.selectionClickUntil = Date.now() + 500;
        return options.onSelect(suggestion, query);
      },
      onSubmit: options.onSubmit,
      onInput: options.onInput,
      onError: options.onError,
      minQueryLength: options.minQueryLength,
      debounceMs: options.debounceMs,
    });
    if (button) button.dataset.searchReady = 'true';
    this.connect();
  }

  connect(): void {
    if (!this.input) return;
    this.button?.addEventListener('click', this.onButtonClick);
    this.input.addEventListener('keydown', this.onInputKeydown);
    document.addEventListener('click', this.onDocumentClick);
  }

  setSearchValue(value: string): void {
    if (this.input) this.input.value = value;
  }

  blurSearch(): void {
    this.input?.blur();
  }

  focusSearch(): void {
    selectTextInput(this.input);
  }

  prefillSearch(query: string): void {
    if (!this.input) return;
    this.input.value = query;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.focus();
  }

  closeSearch(): void {
    this.autocomplete?.close();
  }

  submit(): void {
    if (!this.input || !this.submitSearch) return;
    void (this.submitButtonSearch ?? this.submitSearch)(this.input.value);
  }

  dismissIfOutside(event: MouseEvent): void {
    if (event.target instanceof Node && this.interactionRoot?.contains(event.target)) return;
    if (Date.now() < this.selectionClickUntil) {
      this.selectionClickUntil = 0;
      return;
    }
    this.closeSearch();
    this.blurSearch();
    this.dismissSearch?.();
  }

  destroy(): void {
    this.closeSearch();
    this.button?.removeEventListener('click', this.onButtonClick);
    document.removeEventListener('click', this.onDocumentClick);
  }
}
